import { guests as mockGuests, type Guest } from '@/mocks/guests';
import { type Donor } from '@/mocks/partners';
import { mediaItems as mockMedia, type MediaItem } from '@/mocks/media';
import { events as mockEvents, type GtraEvent } from '@/mocks/events';
import { staffMembers as mockStaff, type StaffMember } from '@/mocks/staffData';
export type { StaffMember } from '@/mocks/staffData';

const KEYS = {
  guests: 'gtra_admin_guests',
  donors: 'gtra_admin_donors',
  media: 'gtra_admin_media',
  registrations: 'gtra_admin_registrations',
  applications: 'gtra_admin_applications',
  events: 'gtra_admin_events',
  staff: 'gtra_admin_staff',
  currentStaffId: 'gtra_admin_staff_id',
};

function getOrInit<T>(key: string, defaults: T[]): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T[];
    localStorage.setItem(key, JSON.stringify(defaults));
    return defaults;
  } catch {
    return defaults;
  }
}

function save<T>(key: string, data: T[]): void {
  localStorage.setItem(key, JSON.stringify(data));
}

// ---- Guests ----
export function getGuests(): Guest[] {
  return getOrInit<Guest>(KEYS.guests, mockGuests);
}
export function saveGuests(data: Guest[]): void {
  save(KEYS.guests, data);
}

// ---- Donors ----
export function getDonors(): Donor[] {
  return getOrInit<Donor>(KEYS.donors, []);
}
export function saveDonors(data: Donor[]): void {
  save(KEYS.donors, data);
}

// ---- Media ----
export function getMedia(): MediaItem[] {
  return getOrInit<MediaItem>(KEYS.media, mockMedia);
}
export function saveMedia(data: MediaItem[]): void {
  save(KEYS.media, data);
}

// ---- Registrations ----
export interface Registration {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  eventId: string;
  eventName: string;
  registeredAt: string;
  status: 'pending' | 'approved' | 'rejected';
  notes?: string;
}
export function getRegistrations(): Registration[] {
  return getOrInit<Registration>(KEYS.registrations, []);
}
export function saveRegistrations(data: Registration[]): void {
  save(KEYS.registrations, data);
}

// ---- Applications ----
export interface Application {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  type: string;
  region: string;
  description: string;
  appliedAt: string;
  status: 'pending' | 'approved' | 'rejected';
}
export function getApplications(): Application[] {
  return getOrInit<Application>(KEYS.applications, []);
}
export function saveApplications(data: Application[]): void {
  save(KEYS.applications, data);
}

// ---- Admin Events ----
export interface AdminEvent extends GtraEvent {
  maxCapacity?: number;
  registrationStart?: string;   // ISO date string, e.g. "2026-03-01"
  registrationDeadline?: string; // ISO date string, e.g. "2026-06-30"
}

export function getAdminEvents(): AdminEvent[] {
  return getOrInit<AdminEvent>(KEYS.events, mockEvents);
}
export function saveAdminEvents(data: AdminEvent[]): void {
  save(KEYS.events, data);
}

// ---- Staff ----
/** Migrate legacy single-role to roles[] */
function migrateStaff(data: StaffMember[]): StaffMember[] {
  return data.map((s) => {
    const legacy = s as StaffMember & { role?: string };
    if (!s.roles && legacy.role) {
      return { ...s, roles: [legacy.role as import('@/utils/rbac').RoleKey] };
    }
    if (!s.roles) return { ...s, roles: ['viewer' as import('@/utils/rbac').RoleKey] };
    return s;
  });
}

export function getStaff(): StaffMember[] {
  const data = getOrInit<StaffMember>(KEYS.staff, mockStaff);
  return migrateStaff(data);
}
export function saveStaff(data: StaffMember[]): void {
  save(KEYS.staff, data);
}

// ---- Current Session ----
export function getCurrentStaffId(): string | null {
  return localStorage.getItem(KEYS.currentStaffId);
}
export function setCurrentStaffId(id: string | null): void {
  if (id) localStorage.setItem(KEYS.currentStaffId, id);
  else localStorage.removeItem(KEYS.currentStaffId);
}
export function getCurrentStaff(): StaffMember | null {
  const all = getStaff();
  const id = getCurrentStaffId();
  if (!id) {
    return all.find((s) => s.roles?.includes('super_admin')) ?? null;
  }
  return all.find((s) => s.id === id) ?? null;
}
